import { useLayoutEffect, useRef } from 'react';
import { cx } from './primitives/cx';

export interface SectionTab {
  id: string;
  label: string;
  /** Cantidad junto al nombre («Por revisar 3»). */
  count?: number;
}

/**
 * P-075 · Las vistas de una sección (Inventario: Stock · Movimientos · Traspasos…), con la piel del
 * rediseño. Una sola fila que se desplaza de lado en el teléfono, en vez de tres renglones de botones:
 * la lista queda más arriba. La pestaña activa se trae a la vista (solo de lado: la página no salta).
 *
 * Son destinos dentro de la pantalla, no un control de formulario: cada uno es un botón y el activo lleva
 * `aria-current`. Sin `gap` de flexbox (Chromium 83): la separación es de margen (`screens.css`).
 */
export function SectionTabs({ label, tabs, active, onSelect, className }: {
  label: string;
  tabs: SectionTab[];
  active: string;
  onSelect: (id: string) => void;
  className?: string;
}) {
  const scrollerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    const current = scroller?.querySelector<HTMLElement>('[aria-current="true"]');
    if (!scroller || !current) return;
    const left = current.offsetLeft - scroller.offsetLeft;
    const right = left + current.offsetWidth;
    if (left < scroller.scrollLeft) scroller.scrollLeft = left;
    else if (right > scroller.scrollLeft + scroller.clientWidth) scroller.scrollLeft = right - scroller.clientWidth;
  }, [active]);

  return (
    <nav aria-label={label} className={cx('n3-tabs', className)}>
      <div className="n3-tabs__scroller" ref={scrollerRef}>
        {tabs.map((tab) => (
          <button
            aria-current={tab.id === active ? 'true' : undefined}
            className={cx('n3-tabs__tab', tab.id === active && 'n3-tabs__tab--active')}
            key={tab.id}
            onClick={() => onSelect(tab.id)}
            type="button"
          >
            <span className="n3-tabs__label">{tab.label}</span>
            {tab.count ? <span className="n3-tabs__count">{tab.count}</span> : null}
          </button>
        ))}
      </div>
    </nav>
  );
}
